import React, { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, Segmented, TableWrap, Th, Td, MarketTag } from '../components/ui/bits';
import { DailySplit } from '../components/charts/DailySplit';
import { useRpc, useSel } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { VYO_LIMITS } from '../lib/business';
import { agoLabel, fmtDateTime, fmtDate } from '../lib/format';
import { sum } from '../lib/market';
import type { VyoResp } from '../lib/types';
import { useIsNarrow } from '../hooks/useIsNarrow';

/** Vyo usage from coach_messages (role user). Limit pressure from coach_usage_log, which is what coach-chat enforces. */
export const VyoPage: React.FC = () => {
  const [days, setDays] = useState('30');
  const { data: v, error, isLoading } = useRpc<VyoResp>('admin_dash_vyo', { p_days: Number(days) });
  const [open, setOpen] = useState<string | null>(null);
  const narrow = useIsNarrow(1023);
  const transcriptRef = useRef<HTMLDivElement>(null);
  // Below 1024px the transcript sits under the whole list, so picking a conversation has to bring it into view.
  useEffect(() => {
    if (open && narrow) transcriptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const { data: msgs, error: msgErr } = useSel<any[]>(['vyo-msgs', open], () =>
    supabase.from('coach_messages').select('role, content, action, created_at').eq('conversation_id', open!).order('created_at').limit(200),
    { enabled: !!open });

  if (isLoading) return <LoadingRow label="Loading Vyo" />;
  if (error || !v) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_vyo" />;

  const t = v.totals;
  const win = sum(t.window);
  const senders = sum(t.senders_window);

  return (
    <div>
      <PageHeader
        title="Vyo"
        subtitle={<>Every message a person sent to Vyo, by London day and market. Internal accounts excluded. Paid plans get {VYO_LIMITS.subscription_monthly} a month (resets on the 1st, UTC); free accounts get {VYO_LIMITS.free_lifetime} in total; firm packs add their own allowance.</>}
        actions={<Segmented label="Window" value={days} onChange={setDays} options={[{ value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]} />}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <StatTile label="Messages today" value={sum(t.today)} split={t.today} source="coach_messages, role user" />
        <StatTile label="Messages, 7 days" value={sum(t.d7)} split={t.d7} sub={`${sum(t.senders_7d)} people`} />
        <StatTile label={`Messages, ${v.days} days`} value={win.toLocaleString('en-GB')} split={t.window}
          sub={`${senders} people, ${senders ? (win / senders).toFixed(1) : '0'} each`} />
        <StatTile label="This calendar month" value={sum(t.month).toLocaleString('en-GB')} split={t.month} sub={`Since ${fmtDate(v.month_start)}`} />
        <StatTile label="In firm pack chats" value={t.pack_window} sub={`${win ? Math.round((t.pack_window / win) * 100) : 0}% of the window, ${t.conversations_window} conversations`} />
        <StatTile label="All time" value={sum(t.all_time).toLocaleString('en-GB')} split={t.all_time} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatTile label="Free users at the limit" value={v.cap.free_at_cap} tone={v.cap.free_at_cap ? 'warn' : 'default'}
          sub={`${VYO_LIMITS.free_lifetime} lifetime messages used`} source="coach_usage_log, firm chats excluded" />
        <StatTile label="Free users close to it" value={v.cap.free_near_cap} sub="5 to 7 used" source="coach_usage_log" />
        <StatTile label="Paid users over 150 this month" value={v.cap.paid_over_150} source="coach_usage_log since the 1st, UTC" />
        <StatTile label="Paid users at 200" value={v.cap.paid_at_cap} tone={v.cap.paid_at_cap ? 'warn' : 'default'} source="coach_usage_log since the 1st, UTC" />
      </div>

      <Section title={`Messages per day, ${v.days} days`} source="coach_messages, role user" className="mb-3">
        <DailySplit data={v.daily} unit="messages" />
      </Section>

      <Section title="Heaviest users" sub={`Messages in the last ${v.days} days. Limit used is what coach-chat counts against the plan: lifetime for free, this month for paid. From packs is Vyo paid by a firm pack, which coach-chat spends first and doesn't count against the limit.`} source="coach_messages and coach_usage_log" className="mb-3">
        {v.top_users.length === 0 ? <EmptyNote>No messages in the window</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>User</Th><Th>Market</Th><Th>Tier</Th><Th className="text-right">Window</Th><Th className="text-right">In pack chats</Th><Th className="text-right">Lifetime</Th><Th>Limit used</Th><Th className="text-right">From packs</Th></tr></thead>
            <tbody>
              {v.top_users.map(u => {
                const pct = Math.min(100, (u.ledger_used / u.ledger_limit) * 100);
                return (
                  <tr key={u.user_id}>
                    <Td><UserLink id={u.user_id} label={u.email} /></Td>
                    <Td><MarketTag market={u.market} /></Td>
                    <Td><Badge tone={u.paid_tier ? 'emerald' : 'stone'}>{u.paid_tier ? 'Paid' : 'Free'}</Badge></Td>
                    <Td className="text-right tabular-nums font-medium text-stone-900">{u.n_window}</Td>
                    <Td className="text-right tabular-nums">{u.n_pack}</Td>
                    <Td className="text-right tabular-nums">{u.n_lifetime}</Td>
                    <Td>
                      <div className="flex items-center gap-2 sm:min-w-[160px]">
                        <div className="flex-1 h-1.5 bg-stone-100 rounded-sm overflow-hidden">
                          <div className={`h-full rounded-sm ${u.ledger_used >= u.ledger_limit ? 'bg-amber-500' : 'bg-indigo-600'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[12px] tabular-nums text-stone-600 w-16 text-right">{u.ledger_used} of {u.ledger_limit}</span>
                      </div>
                    </Td>
                    <Td className="text-right tabular-nums">{u.pack_vyo_used ?? 0}</Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
        <p className="text-[12px] text-stone-500 mt-2">
          A free user can show more messages than their limit when turns were paid by a pack, when a reply failed and coach-chat released the reservation, or for messages before about 19 Oct 2026, when coach-chat began writing every turn to its usage log.
        </p>
      </Section>

      <div className="grid lg:grid-cols-2 gap-3">
        <Section title="Latest conversations" sub="Newest 40. Pick one to read it." source="coach_conversations">
          {v.conversations.length === 0 ? <EmptyNote>No conversations</EmptyNote> : (
            <div className="divide-y divide-stone-100">
              {v.conversations.map(c => (
                <button key={c.id} onClick={() => setOpen(open === c.id ? null : c.id)}
                  className={`w-full flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-1 text-left px-2 py-2 rounded-md text-[13px] ${open === c.id ? 'bg-indigo-50' : 'hover:bg-stone-50'}`}>
                  <span className="min-w-0 w-full sm:w-auto">
                    <span className="block truncate text-stone-900 font-medium">{c.title || 'Untitled'}</span>
                    <span className="block truncate text-[12px] text-stone-500">{c.email ?? c.user_id.slice(0, 8)}{c.firm_name ? ` · ${c.firm_name} pack` : ''}</span>
                  </span>
                  <span className="flex items-center gap-3 shrink-0 text-[12px] text-stone-500">
                    <MarketTag market={c.market} />
                    <span className="tabular-nums">{c.user_messages} sent</span>
                    <span>{agoLabel(c.updated_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Section>
        <div ref={transcriptRef} className="scroll-mt-3">
        <Section title="Transcript" source="coach_messages">
          {!open ? <EmptyNote>Select a conversation</EmptyNote> : msgErr ? <ErrorPanel error={msgErr} /> : (
            <div className="space-y-2 max-h-[75dvh] lg:max-h-[560px] overflow-y-auto">
              {(msgs ?? []).map((m, i) => (
                <div key={i} className={`p-2.5 rounded-md text-[12.5px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-stone-50 text-stone-800' : 'bg-indigo-50 text-stone-900'}`}>
                  <span className="block text-[11.5px] font-medium mb-0.5 text-stone-500">{m.role === 'user' ? 'User' : 'Vyo'} · {fmtDateTime(m.created_at)}</span>
                  {String(m.content)}
                  {m.action && (
                    <span className="mt-1.5 inline-flex rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-[11.5px] font-medium text-indigo-700">
                      Card: {m.action.label ?? 'action'} opens {m.action.tab ?? m.action.section ?? 'a page'}{m.action.locked ? ', locked' : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
        </div>
      </div>
    </div>
  );
};
